import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { FileDragger } from '@bingads-webui-campaign-react/file-dragger';
import { FilePicker as BackboneFilePicker } from '@bingads-webui/file-picker';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { parseFileName } from '../utils/parse-file';

export class FilePicker extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      i18n,
      enableMultiple,
      allowedFileExtensions,
      showExtensionError,
      useButton,
    } = props;
    const filePickerOptions = {
      allowedFileExtensions,
      i18n,
      isNative: false,
      displayFilePath: 'filename',
      enableMultiple,
      useButton,
    };

    // This dependency injection is only for test purpose
    // For the implementation is coupled tightly with BackboneFilePicker
    this.filePicker = this.props.filePicker ? this.props.filePicker : new BackboneFilePicker(filePickerOptions);

    this.filePicker.on('validated', this.readFile);
    this.filePicker.on('error.extension', () => { showExtensionError(); });

    this.filePicker.render();
    this.filePicker.$('.remove-file').remove();
    this.filePicker.$('.file-text').hide();

    this.state = {
      isFileDraging: false,
    };

    // Only for test purpose
    if (this.props.testRef) {
      this.props.testRef.current = {
        filePicker: this.filePicker,
        readFile: this.readFile.bind(this),
        getReachFileSizeLimitErrorMessage: this.props.getReachFileSizeLimitErrorMessage.bind(this),
      };
    }
  }

  readFile = (fileObjects) => {
    const {
      onFileRead,
      showErrors,
      onFileSelected,
      maximumFileSizeMB,
      isReachFileSizeLimit,
      getReachFileSizeLimitErrorMessage,
    } = this.props;

    const selectedFiles = !_.isUndefined(fileObjects) && fileObjects.length > 0 ? fileObjects :
      this.filePicker.fileObjects;

    if (selectedFiles) {
      const selectedFilesCount = selectedFiles.length;
      let fileIndex;
      const errorArr = [];

      for (fileIndex = 0; fileIndex < selectedFilesCount; fileIndex += 1) {
        const fileObj = selectedFiles[fileIndex];
        const fileSizeMB = (fileObj.size / 1024 / 1024).toFixed(3);
        let reachLimit;

        if (isReachFileSizeLimit) {
          const { ext } = parseFileName(fileObj.name);
          reachLimit = isReachFileSizeLimit({ ext, fileSizeMB });
        } else {
          reachLimit = maximumFileSizeMB > 0 && fileSizeMB > maximumFileSizeMB;
        }

        if (reachLimit) {
          errorArr.push(getReachFileSizeLimitErrorMessage(_.extend({
            fileSizeMB,
          }, !isReachFileSizeLimit && { maximumFileSizeMB })));

          break;
        }
      }

      if (_.isEmpty(errorArr)) {
        if (_.isFunction(onFileSelected)) {
          onFileSelected(selectedFiles);
        }

        return onFileRead(selectedFiles);
      }

      showErrors(errorArr);
    } // else no media picked, so do nothing

    return ''; // for testability
  };

  handleDraging = (isDraging) => {
    this.setState({
      isFileDraging: isDraging,
    });
  };

  render() {
    const {
      i18n,
      enableDrag,
      hideDescription,
      allowedFileExtensions,
      showExtensionError,
      fileDraggerLabel,
      fileDraggerTip,
      fileDraggerDropDescription,
      useLargeStyling,
    } = this.props;

    const {
      isFileDraging,
    } = this.state;

    const fileButton = (
      <div className={`file-picker-react-wrapper ${isFileDraging ? 'disabled' : ''}`}>
        <BackboneInstanceWrapper
          instance={this.filePicker}
        />
      </div>
    );

    return (
      <div className="file-picker-container">
        {!hideDescription &&
          <div className="choose-file-description">
            {i18n.getString('ImageExperience_Choose_File_Description')}
          </div>
        }
        {
          !enableDrag ?
            <div className="file-picker-react-wrapper">
              <BackboneInstanceWrapper
                instance={this.filePicker}
              />
            </div> :
            <div className="file-drag-container">
              <FileDragger
                onReadFile={this.readFile}
                allowedFileExtension={allowedFileExtensions}
                onInvalidExtension={showExtensionError}
                label={fileDraggerLabel}
                description={fileDraggerTip}
                dropDescription={fileDraggerDropDescription}
                dragging={this.handleDraging}
                i18n={i18n}
                fileButton={fileButton}
                useLargeStyling={useLargeStyling}
              />
            </div>
        }
      </div>
    );
  }
}

FilePicker.propTypes = {
  onFileRead: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  showErrors: PropTypes.func.isRequired,
  enableMultiple: PropTypes.bool,
  enableDrag: PropTypes.bool,
  hideDescription: PropTypes.bool,
  fileDraggerLabel: PropTypes.string,
  fileDraggerTip: PropTypes.string,
  fileDraggerDropDescription: PropTypes.string,
  onFileSelected: PropTypes.func,
  allowedFileExtensions: PropTypes.arrayOf(PropTypes.string),
  showExtensionError: PropTypes.func,

  maximumFileSizeMB: PropTypes.number,
  isReachFileSizeLimit: PropTypes.func,
  getReachFileSizeLimitErrorMessage: PropTypes.func,
  useLargeStyling: PropTypes.bool,
  useButton: PropTypes.bool,
};

FilePicker.defaultProps = {
  enableMultiple: false,
  enableDrag: false,
  hideDescription: false,
  onFileSelected: _.noop(),
  allowedFileExtensions: [],
  showExtensionError: _.noop,

  fileDraggerLabel: '',
  fileDraggerTip: '',
  fileDraggerDropDescription: '',

  // below two are conflict and isReachFileSizeLimit takes higher priority
  isReachFileSizeLimit: undefined,
  maximumFileSizeMB: undefined,
  getReachFileSizeLimitErrorMessage: _.noop,
  useLargeStyling: false,
  useButton: false,
};
